import { useQuery } from '@tanstack/react-query'
import { ApiError, hasLiveApi } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import {
  fixtureOneAccount,
  fromGetOneAccount,
  getOneAccount,
  type OneAccountView,
} from '@/api/map/oneAccount'
import { keys } from '@/api/hooks/shared/keys'
import type { PlStatementRowView } from '@/api/map/plStatement'

async function loadOneAccount(row: PlStatementRowView): Promise<OneAccountView> {
  if (!hasLiveApi()) return fixtureOneAccount(row)
  try {
    const response = await getOneAccount(row.id)
    return fromGetOneAccount(response, row)
  } catch (error) {
    if (error instanceof ApiError && error.status === 403) {
      return { ...fixtureOneAccount(row), bets: [], unavailable: 'forbidden' }
    }
    if (isUnreachableApiError(error)) {
      return { ...fixtureOneAccount(row), bets: [], unavailable: 'empty' }
    }
    throw error
  }
}

/**
 * Lab `Account/getOneAccount` — P&L row + bets for the Game Detail drawer.
 */
export function useOneAccount(row: PlStatementRowView | null) {
  return useQuery({
    queryKey: keys.oneAccount(row?.id ?? ''),
    queryFn: () => loadOneAccount(row!),
    enabled: Boolean(row?.id),
    retry: false,
  })
}
